<template>
	<LockedStepByMenu v-if="showMenu && showLockedIndication" :lockedCollaboratorName="lockedConnectionOwner" />
	<LockedStepBy v-else-if="showLockedIndication" :lockedCollaboratorName="lockedConnectionOwner" />
</template>
<script>
import api from '@/api';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { mapActions, mapGetters } from 'vuex';
import CredentialsService from '@/utils/credentials-service';
import { teamMembers } from '@/store/modules/account/getters';
import { getAccountMembers } from '@/store/modules/account/actions';
import { selectedProjectId, selectedProject } from '@/store/modules/selectedProject/getters';
import { selectedWorkflowId } from '@/store/modules/selectedWorkflow/getters';
import LockedStepBy from '@/components/BlockEditor/LockedStep/LockedStepBy.vue';
import LockedStepByMenu from '@/components/BlockEditor/LockedStep/LockedStepByMenu.vue';
import { getWorkflowLockedCredentials } from '@/store/modules/credentials/actions';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { getCredentialsForService } from '@/store/modules/apps/getters';
import { VIRTUAL_PROFILES_IDS, PER_USER_CONNECTION_ID, PER_ACCOUNT_CONNECTION_ID } from '@/utils/credentials-service.const';
import { mapGettersShorthand } from '@/store/utils/mappers';
import ProjectService from '@/utils/projects-service';
import { CredentialsTypes, UserCredentialsStatus, ProjectPermissions } from '@ply-io/ply-front-shared-utilities';
export default {
	name: 'LockedStepDataProvider',
	props: {
		profileId: { type: Number },
		serviceId: { type: Number },
		showMenu: { type: Boolean },
		credentialsType: { type: String, default: CredentialsTypes.Embedded },
	},
	components: {
		LockedStepBy,
		LockedStepByMenu,
	},
	async mounted() {
		if (this.isLocked && this[selectedWorkflowId.fullPath]) {
			await this[getAccountMembers.fullPath]();
			await this.setLockedCredentialData(this.selectedProfileId);
		} else {
			this.initialized = true;
		}
	},
	computed: {
		...mapGettersShorthand({ selectedProject }),
		...mapGetters([
			teamMembers.fullPath,
			getCredentialsForService.fullPath,
			projectHasPermission.fullPath,
			selectedProjectId.fullPath,
			selectedWorkflowId.fullPath,
		]),
		showLockedIndication() {
			if (this.isSelectedProjectPersonal) {
				// Always hide indication if private space
				return false;
			}
			return this.profileId && this.serviceId && this.initialized && (this.isLockedByMe || this.lockedConnectionOwner);
		},
		projectId() {
			return this[selectedProjectId.fullPath];
		},
		isSelectedProjectPersonal() {
			return ProjectService.isPersonalProject(this.selectedProject);
		},
		profileItemsList() {
			if (!this.serviceId) {
				return [];
			}
			const credentialsForService = this[getCredentialsForService.fullPath](this.serviceId) || [];

			const realProfiles = [...credentialsForService];

			let realProfilesInProject = realProfiles;
			if (this.projectId && !isNaN(this.projectId)) {
				realProfilesInProject = realProfiles.filter(
					(x) => !x.is_related || CredentialsService.isConnectionRelatedToProject(x, this.projectId)
				);
			}
			return realProfilesInProject;
		},

		selectedProfileIsSensitive() {
			return CredentialsService.isConnectionRelatedToProjectAndSensitive(this.selectedProfile, this.projectId);
		},
		hasSensitivePermissions() {
			if (!this.selectedProfile?.access?.related_entity_id) {
				return false;
			}
			return this[projectHasPermission.fullPath](
				this.selectedProfile.access.related_entity_id,
				ProjectPermissions.ViewSensitiveCredentials
			);
		},
		isLocked() {
			return (
				(this.selectedProfileId && isEmpty(this.selectedProfile)) || (this.selectedProfileIsSensitive && !this.hasSensitivePermissions)
			);
		},
		isLockedByMe() {
			return this.selectedProfileId && this.selectedProfile?.access?.related_entity_type !== 'project';
		},
		selectedProfile() {
			let lookedUpId = this.selectedProfileId;
			if (CredentialsService.isConnectionFromConfiguration(this.credentialsType)) {
				if (this.credentialsType === CredentialsTypes.ProfileBased) {
					lookedUpId = PER_USER_CONNECTION_ID;
				} else if (this.credentialsType === CredentialsTypes.AccountBased) {
					lookedUpId = PER_ACCOUNT_CONNECTION_ID;
				}
			}
			return this.profileItemsList.find((profile) => profile.id === lookedUpId) || {};
		},
		selectedProfileId: {
			get() {
				if (this.showDetailsDialog) {
					return this.reconnectingCredsId;
				} else if (this.isTestingProfile) {
					return this.checkingProfileId;
				} else if (this.credentialsType === CredentialsTypes.Embedded) {
					return this.profileId;
				} else if (this.credentialsType === CredentialsTypes.ProfileBased) {
					return PER_USER_CONNECTION_ID;
				} else if (this.credentialsType === CredentialsTypes.AccountBased) {
					return PER_ACCOUNT_CONNECTION_ID;
				}
				// we shouldn't be here, which credentialsType is defined?
				console.warn('credentials type of node is unknown');
				return null;
			},
			async set(value) {
				await this.connectProfile(value);
			},
		},

		lockedConnectionOwner() {
			if (!this.isLocked) {
				return null;
			}
			if (!this.lockedCredentialData?.user) {
				return '';
			}
			return this[teamMembers.fullPath].find((x) => x.user_id === this.lockedCredentialData?.user)?.name || '';
		},
		lockedConnectionIsSensitive() {
			return CredentialsService.isConnectionRelatedToProjectAndSensitive(this.lockedCredentialData, this.projectId);
		},
	},
	methods: {
		...mapActions([getWorkflowLockedCredentials.fullPath, getAccountMembers.fullPath]),

		async checkProfile(profileId) {
			if (this.selectedProfile?.status === UserCredentialsStatus.Staled) {
				return {
					success: false,
					errorJSON: this.selectedProfile.error_message,
				};
			} else {
				this.checkingProfileId = profileId;
				this.isTestingProfile = true;
				let ret;
				try {
					ret = await api.testConnection(profileId);
					return ret;
				} catch (error) {
					ret = {
						success: false,
						errorJSON: 'Error occured while testing credentials',
					};
				} finally {
					this.isTestingProfile = false;
					this.checkingProfileId = null;
				}
				return ret;
			}
		},

		async connectProfile(value, forceSet) {
			if ((value && value !== this.profileId) || forceSet) {
				let profileToSet;
				let validToSet = VIRTUAL_PROFILES_IDS.includes(value);
				let r = {};
				if (!validToSet) {
					// checking profile
					r = await this.checkProfile(value);
					validToSet = r.success;
				}
				// profile is valid
				if (validToSet) {
					this.resetErrorData();
					profileToSet = value;
					this.$emit('profile-set-successful');
				} else {
					this.$emit('profile-set-failed');
					this.profileErr = r.errorJSON;
					this.showDetailsDialog = true;
					this.reconnectingCredsId = value;
					profileToSet = null; // Reset.
				}
				this.$emit('select-profile', profileToSet);
			}
		},

		isObject(item) {
			return isObject(item);
		},
		resetErrorData() {
			this.reconnectingCredsId = null;
			this.profileErr = null;
		},
		async setLockedCredentialData(selectedProfileId) {
			if (selectedProfileId && ![PER_USER_CONNECTION_ID, PER_ACCOUNT_CONNECTION_ID].includes(selectedProfileId)) {
				this.lockedCredentialData = await this[getWorkflowLockedCredentials.fullPath]({
					workflowId: this[selectedWorkflowId.fullPath],
					credentialsId: selectedProfileId,
				});
			}
			this.initialized = true;
		},
	},
	data: () => ({
		reconnectingCredsId: null,
		profileErr: null,
		isTestingProfile: false,
		checkingProfileId: null,
		showDetailsDialog: false,
		lockedCredentialData: {},
		initialized: false,
	}),
	watch: {
		showLockedIndication: {
			handler(newV) {
				this.$emit('showing-locked-indication', { isShowing: newV, isLockedByMe: !this.lockedConnectionOwner?.length > 0 });
			},
			immediate: true,
		},
		showDetailsDialog: {
			handler(newV) {
				if (!newV) {
					// when closing details dialog, reset the related error data
					this.resetErrorData();
				}
			},
		},
		isTestingProfile(newV) {
			this.$emit('set-testing-profile', newV);
		},
		async selectedProfileId(newV, oldV) {
			if (oldV !== newV && !this.profileItemsList.find((x) => x.id === newV)) {
				await this.setLockedCredentialData(newV);
			}
		},
	},
};
</script>

<style scoped lang="scss"></style>
